import { useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { ReactElement, VoidFunctionComponent } from 'react';
import type { SelectCallback } from 'react-bootstrap';

import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { useOnMount } from '../../../../hooks';
import { useRoles, useUserInvitations } from '../../../../hooks/api';
import useOrganization from '../../../../hooks/api/organizationV2/useOrganization';
import useOrganizationAccessRequests from '../../../../hooks/api/organizationV2/useOrganizationAccessRequests';
import OrganizationAccessRequestsModule from '../../../../redux/organization_access_requests';
import TeamsModule from '../../../../redux/teams';
import UsersModule from '../../../../redux/users';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import Loader from '../../../generic/Loader';
import Tabs from '../../../generic/Tabs';
import { ModalInviteUsers } from '../../../modals/ModalInviteUsers';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import type { Role } from '../../../../../shared/model-typings';
import type { MinimalOrganization, MinimalOrganizationAccessRequest } from '../../../../../shared/organizationV2';
import type { ReduxStore } from '../../../../redux/store';
import type { UsersStore } from '../../../../redux/users';
import type { AsyncStatus } from '../../../../types';

import { NONE_ROLE } from './constants';
import OrganizationAccessRequestsTable from './OrganizationAccessRequestsTable';
import UserInvitationsTable from './UserInvitationsTable';
import UsersTable from './UsersTable';

type Tab = 'members' | 'requests' | 'invites';

type OwnProps = {
  organization: MinimalOrganization;
  organizationAccessRequests: MinimalOrganizationAccessRequest[];
};

type ReduxStateProps = {
  users: UsersStore['users'];
};

type DispatchProps = {
  inviteExistingAccessRequest: (organizationId: number, accessRequestId: number) => void;
  removeAccessRequest: (organizationId: number, accessRequestId: number) => void;
};

type OrganizationUsersIndexProps = OwnProps & ReduxStateProps & DispatchProps;

const OrganizationUsersIndex = ({
  users,
  inviteExistingAccessRequest,
  organization,
  organizationAccessRequests,
  removeAccessRequest,
}: OrganizationUsersIndexProps) => {
  const { data: roles = [] } = useRoles();
  //  Permissions
  const hasPermission = useHasPermission();
  const canInviteUser = hasPermission({ action: Action.Invite, resourceType: ResourceType.User });

  const [searchValue, setSearchValue] = useState('');
  const [shouldShowInviteModal, setShouldShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [orgRoleFilter, setOrgRoleFilter] = useState<Role | 'None' | null>(null);
  const { data: userInvitations, isLoading, isError, error } = useUserInvitations(canInviteUser);

  const onSetActiveTabFn = (tab: Tab) => () => {
    setActiveTab(tab);
    setSearchValue('');
  };

  //  Handle changing the role filter dropdown.
  const onRoleFilterDropdownSelect = (roleId: number | 'None' | 'clear') => {
    //  'None' is for users who have no org role, `null` is for when users clear the Dropdown.
    if (roleId === NONE_ROLE || roleId === 'clear') {
      return setOrgRoleFilter(roleId === 'clear' ? null : roleId);
    }
    const selectedRole = roles.find((role) => role.id === roleId);
    return setOrgRoleFilter(selectedRole || null);
  };

  // Only show active access requests (those that haven't been accepted yet).
  const allUserEmails = new Set(Object.values(users).map((user) => user.email));
  const activeAccessRequests = organizationAccessRequests?.filter((accessRequest) => {
    return !allUserEmails.has(accessRequest.email);
  });

  let body: ReactElement;
  switch (activeTab) {
    case 'requests':
      body = (
        <OrganizationAccessRequestsTable
          accessRequests={activeAccessRequests}
          searchQuery={searchValue}
          onAcceptRequest={requestId => inviteExistingAccessRequest(organization.id, requestId)}
          onIgnoreRequest={requestId => removeAccessRequest(organization.id, requestId)}
        />
      );
      break;
    case 'invites':
      body = (
        <UserInvitationsTable
          invitations={userInvitations ?? []}
          users={users}
          searchQuery={searchValue}
        />
      );
      break;
    case 'members':
    default:
      body = (
        <UsersTable
          users={users}
          searchQuery={searchValue}
          orgRoleFilter={orgRoleFilter}
        />
      );
  }

  const shouldShowTabs = window.on_prem || (canInviteUser && !organization.disable_invite);
  const orgRoles = roles.filter((role) => role.scope === 'organization');

  return (
    <>
      <Header heading="Users">
        {isError && (
          <Header.Errors errors={[error.message]} />
        )}
      </Header>
      {shouldShowTabs && (
        <Tabs>
          <Tabs.Tab
            label="Members"
            isActive={activeTab === 'members'}
            onSelect={onSetActiveTabFn('members')}
            badgeCount={Object.values(users).length}
          />
          {window.on_prem && (
            <Tabs.Tab
              label="Requests"
              isActive={activeTab === 'requests'}
              onSelect={onSetActiveTabFn('requests')}
              badgeCount={activeAccessRequests.length}
            />
          )}
          {canInviteUser && !organization.disable_invite && (
            <Tabs.Tab
              label="Invites"
              isActive={activeTab === 'invites'}
              onSelect={onSetActiveTabFn('invites')}
              badgeCount={isLoading ? 0 : userInvitations?.length ?? 0}
            />
          )}
        </Tabs>
      )}
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="invite-users-button"
          size="large"
          type="secondary"
          onClick={() => setShouldShowInviteModal(true)}
          isDisabled={!canInviteUser}
          showTooltip={!canInviteUser}
          tooltip="You are not permitted to invite users."
        >
          Invite Users
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.DropdownButton
          id="user-roles-filter"
          title={orgRoleFilter === NONE_ROLE ? NONE_ROLE : orgRoleFilter?.name || 'Organization Role'}
          onSelect={onRoleFilterDropdownSelect as SelectCallback}
          size="large"
        >
          <SettingsPageTableActions.DropdownButton.Item eventKey="clear">
            Clear Filter
          </SettingsPageTableActions.DropdownButton.Item>
          {orgRoles.map(role => (
            <SettingsPageTableActions.DropdownButton.Item key={role.id} eventKey={role.id}>
              {role.name}
            </SettingsPageTableActions.DropdownButton.Item>
          ))}
        </SettingsPageTableActions.DropdownButton>
        <SettingsPageTableActions.SearchInput
          label="Search users"
          size="medium"
          placeholder="Search users"
          value={searchValue}
          onChange={setSearchValue}
        />
      </SettingsPageTableActions>
      {body}
      <ModalInviteUsers
        organizationId={organization.id}
        show={shouldShowInviteModal}
        onHide={() => {
          setShouldShowInviteModal(false);
        }}
      />
    </>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  users: UsersModule.selectors.getUsers,
});

const mapDispatchToProps: DispatchProps = {
  inviteExistingAccessRequest: OrganizationAccessRequestsModule.actions.inviteExisting,
  removeAccessRequest: OrganizationAccessRequestsModule.actions.removeAccessRequest,
};

const ConnectedOrganizationUsersIndex = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrganizationUsersIndex);

/**
 * Container component is required for ensuring that organization is fetched before
 * the OrganizationUsersIndex is rendered, because `useUserInvitations` (and other places)
 * in the component rely on Organization being present.
 */
const OrganizationUsersIndexContainer: VoidFunctionComponent<ReduxStateProps & DispatchProps> = (props) => {
  const dispatch = useDispatch();
  const usersStatus = useSelector(UsersModule.selectors.getStatus) as AsyncStatus;

  const { data: organizationResponse, isLoading: isOrganizationLoading } = useOrganization();
  const { data: accessRequestsResponse } = useOrganizationAccessRequests();

  useOnMount(async () => {
    //  Needed to hydrate the User column
    dispatch(UsersModule.actions.fetchAll());
    //  Needed to hydrate the Teams column
    dispatch(TeamsModule.actions.fetchAll());
  });

  if (!organizationResponse || isOrganizationLoading || usersStatus.isLoading) {
    return (
      <div className="padding-top-lg">
        <Loader size={80} />
      </div>
    );
  }

  return (
    <ConnectedOrganizationUsersIndex
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      organization={organizationResponse.organization}
      organizationAccessRequests={accessRequestsResponse?.accessRequests ?? []}
    />
  );
};

export default OrganizationUsersIndexContainer;
